/**
 * Slice 0 / AT-16 — wire-builder selection pipeline (ADR 0005 Q6 + Q6.tail).
 *
 * The multi-stage, policy-driven pipeline that turns paginated structured
 * records from `NS.iabClassifications` into the final canonical fact set
 * the wire-builder projects to BBS+ signed messages, the proprietary
 * `user.ext.ownyou` envelope, and (in AT-17) the OpenRTB segtax-4 envelope.
 *
 * Quality (which signals to disclose) and privacy (how much information is
 * on the wire) are split into separate stages with explicit, named, versioned
 * functions. Caps become a *privacy budget*, not a *quality knob*.
 *
 * Critical ordering rule (ADR 0005 Q6):
 *
 *   per-observation eligibility runs BEFORE aggregation. Stale or low-
 *   confidence observations cannot dilute `disclosure_confidence` for the
 *   aggregated candidate.
 *
 * The pipeline (in order):
 *
 *   paginate                             // batched store reads (configurable)
 *     → drop-record + shape filter      // skip kind === "normalization_drop"
 *     → per-observation eligibility     // min_confidence, max_age_days
 *     → aggregate eligible observations // collapse by canonical id/key
 *     → per-aggregate eligibility       // min_evidence_count after filtering
 *     → disclosure_confidence (v1)      // mean over eligible observations
 *     → signal_score_v1                 // weighted score (named, versioned)
 *     → diversity controls              // tier_2 + tier_3 caps (segments only)
 *     → privacy budget                  // per-family max_facts cap
 *     → projection                      // strips selection metadata
 *
 * Selection metadata (`score`, `rank`, `reason_codes`) is store/wallet-LOCAL
 * — it persists with the canonical fact records in `NS.iabClassifications`
 * and surfaces via GDPR export, but **never** appears in `user.ext.ownyou`,
 * OpenRTB `user.data[]`, or BBS+ signed messages. The `projectToClaimSet`
 * step explicitly strips it.
 *
 * @traces ADR 0005 §"Wire-builder selection pipeline (Q6)",
 *         §"disclosure_confidence aggregation function (Q6.tail)",
 *         §"Selection metadata is store/wallet-LOCAL".
 */

import { NS } from '@ownyou/shared-types';
import type { MemoryStore } from '@ownyou/memory-store';
import type { DisclosurePolicy, ClaimFamilySelection } from '@ownyou/bbs-plus';

// ────────────────────────────────────────────────────────────────────────
// Types — DisclosureFact, AggregatedCandidate, SelectionMetadata.
// ────────────────────────────────────────────────────────────────────────

/**
 * A canonical observation record as written by the reconciler into
 * `NS.iabClassifications`. The wire-builder reads these directly (Option B
 * from the Slice 0 grilling). The reconciler is the sole writer.
 *
 * Two record kinds exist: `observation` (the historical SemanticMemory
 * shape, default when `kind` is absent) and `normalization_drop` (AT-15
 * normalizer rejection — written so GDPR export surfaces drops). The
 * pipeline's first stage filters out `normalization_drop` records; only
 * `observation` records progress.
 */
export interface DisclosureFactRecord {
  /** Discriminant. Absent on legacy SemanticMemory records (treated as 'observation'). */
  kind?: 'observation' | 'normalization_drop';
  /** Audience-segment numeric id (interests / purchase_intent). */
  taxonomy_id?: number;
  /** Section discriminant — interests | purchase_intent | demographics | household. */
  section?: string;
  /** IAB Audience Taxonomy 1.1 grouping label (Gender, Age Range, ...) — demographics + household. */
  grouping_value?: string;
  /** LLM's human-readable value (Male, 25-34, ...). */
  value?: string;
  /** Aggregated confidence (legacy SemanticMemory shape). v1 disclosure_confidence reuses this. */
  confidence?: number;
  /** Aggregated evidence count (legacy SemanticMemory shape). */
  evidence_count?: number;
  /** First observation timestamp (ISO 8601). */
  first_observed?: string;
  /** Most-recent validation timestamp (ISO 8601). */
  last_validated?: string;
  last_updated?: string;
  /** Tier ancestry — embedded by the reconciler from the bundled iab_taxonomy.json. */
  tier_1?: string;
  tier_2?: string;
  tier_3?: string;
  tier_4?: string;
  tier_5?: string;
  /** Source IDs (emails the observation came from). Length feeds source_diversity. */
  source_ids?: string[];
  supporting_evidence?: string[];
  data_source?: string;
  /** Selection metadata persisted alongside the record (write-only here; pipeline never reads this back). */
  selection_metadata?: SelectionMetadata;
  /** AT-15 normalizer drop record fields (only populated when kind === 'normalization_drop'). */
  reason?: string;
  ruleId?: string;
  raw?: string;
}

export type ClaimFamily = 'interests' | 'purchase_intent' | 'user_attribute';

/**
 * Permitted reason codes for selection metadata. The wallet hard-rejects
 * unknown codes (defense-in-depth: selection metadata never ships on the
 * wire, but if a build bug ever lets it slip, the wallet enforces the
 * policy enumeration).
 */
export type ReasonCode = string;

/**
 * Store/wallet-LOCAL selection metadata. Persisted alongside canonical
 * facts in `NS.iabClassifications`; surfaces via GDPR export. **Never**
 * appears in `user.ext.ownyou`, OpenRTB `user.data[]`, or BBS+ signed
 * messages. The wire-builder's `projectToClaimSet` step strips this.
 */
export interface SelectionMetadata {
  /** signal_score_v1 result (≥ 0). */
  score: number;
  /** 1-indexed rank within the claim family. */
  rank: number;
  /** Structural reasons; enumerated by `policy.allowed_reason_codes`. */
  reason_codes: ReasonCode[];
}

/**
 * Per ADR 0005 Q6 Stage 1: an `AggregatedCandidate` collapses 1..N eligible
 * observations of the same canonical `(family, id-or-key)` pair.
 *
 * Aggregation is deterministic: source records are sorted before aggregating
 * (by record id ascending, with ties resolved id-lexicographic). Re-running
 * the pipeline against the same input produces byte-identical output.
 */
export interface AggregatedCandidate {
  family: ClaimFamily;
  /**
   * Canonical id within the claim family.
   *
   *  - For audience-segment (interests, purchase_intent): stringified
   *    `taxonomy_id` (e.g. `"209"`).
   *  - For user_attribute: canonical key (e.g. `"gender"`).
   */
  canonicalId: string;
  /** Selected canonical value (relevant for user_attribute; undefined for audience segments). */
  canonicalValue?: string;
  /** Display value sourced from the most-recent eligible observation that mapped to canonicalValue. */
  displayValue?: string;
  /** Number of eligible observations contributing to this candidate. */
  observationCount: number;
  /** Mean confidence over eligible observations — used as `disclosure_confidence` (v1). */
  meanConfidence: number;
  /** Sum of evidence_count over eligible observations. */
  totalEvidenceCount: number;
  /** Distinct source set (used by signal_score_v1 :: source_diversity). */
  sourceSet: ReadonlySet<string>;
  /** Most-recent `last_validated` across eligible observations (ISO 8601). */
  mostRecentLastValidated: string;
  /** Tier ancestry (audience-segment only) from the most-recent eligible observation. */
  tier_1?: string;
  tier_2?: string;
  tier_3?: string;
  tier_4?: string;
  tier_5?: string;
}

/**
 * The canonical fact emitted by the pipeline. Selection metadata is
 * attached for store-side persistence; `projectToClaimSet` strips it on
 * the wire.
 */
export interface CanonicalFact {
  family: ClaimFamily;
  canonicalId: string;
  canonicalValue?: string;
  displayValue?: string;
  /** disclosure_confidence (v1) — mean over eligible observations. */
  disclosureConfidence: number;
  /** Tier ancestry (audience-segment only). */
  tier_1?: string;
  tier_2?: string;
  tier_3?: string;
  tier_4?: string;
  tier_5?: string;
  /** Store/wallet-LOCAL — never on the wire. */
  selectionMetadata: SelectionMetadata;
}

// ────────────────────────────────────────────────────────────────────────
// Constants — defaults + caps.
// ────────────────────────────────────────────────────────────────────────

/** Defensive ceiling: refuse to consume more than this many records. */
export const RECORD_ANOMALY_CEILING = 100_000;

/** Default page size for paginated reads from `NS.iabClassifications`. */
export const DEFAULT_PAGE_SIZE = 500;

/**
 * Canonical IAB Audience Taxonomy 1.1 numeric id pattern (segtax=4): leading
 * non-zero digit, 1..6 digits. Pre-Slice-0 records with `taxonomy_id: 0` or
 * an out-of-range id like `1234567` are dropped at aggregation time so they
 * never reach the wire. Mirrors `CANONICAL_IAB_RE` in
 * `browser-extension/modules/wallet-module.js`.
 */
const AUDIENCE_TAXONOMY_ID_PATTERN = /^[1-9][0-9]{0,5}$/;

// ────────────────────────────────────────────────────────────────────────
// Stage: pagination.
// ────────────────────────────────────────────────────────────────────────

export interface PaginationOptions {
  pageSize?: number;
  /** Hard ceiling — exceeding raises a structured error. */
  anomalyCeiling?: number;
}

/**
 * Paginated read from `NS.iabClassifications`. Replaces the historical
 * silent `limit: 200, offset: 0` single-call read.
 *
 * @throws Error("AT16_RECORD_ANOMALY_CEILING_EXCEEDED") on >= ceiling records.
 */
export async function paginateClassifications(
  store: MemoryStore,
  userId: string,
  opts: PaginationOptions = {},
): Promise<DisclosureFactRecord[]> {
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const ceiling = opts.anomalyCeiling ?? RECORD_ANOMALY_CEILING;
  const ns = NS.iabClassifications(userId);

  const records: DisclosureFactRecord[] = [];
  let offset = 0;

  // Defensive cap on page count (prevents infinite loops on misbehaving backends).
  const maxPages = Math.ceil(ceiling / pageSize) + 1;
  for (let page = 0; page < maxPages; page++) {
    const result = await store.list<DisclosureFactRecord>(ns, { limit: pageSize, offset });
    if (!result.items || result.items.length === 0) break;
    records.push(...result.items);
    if (records.length > ceiling) {
      throw new Error(
        `AT16_RECORD_ANOMALY_CEILING_EXCEEDED: NS.iabClassifications for user "${userId}" exceeded ${ceiling} records — refusing to consume to bound memory. Investigate the reconciler.`,
      );
    }
    if (result.items.length < pageSize) break;
    offset += pageSize;
  }
  return records;
}

// ────────────────────────────────────────────────────────────────────────
// Stage: drop-record + shape filter.
// ────────────────────────────────────────────────────────────────────────

/**
 * Skip `kind === "normalization_drop"` records (AT-15 normalizer rejections
 * persist to the same namespace so GDPR export surfaces drops). Defensive
 * shape check excludes records without a recognised section.
 */
export function applyDropAndShapeFilter(
  records: readonly DisclosureFactRecord[],
): DisclosureFactRecord[] {
  return records.filter((r) => {
    if (r.kind === 'normalization_drop') return false;
    if (typeof r.section !== 'string') return false;
    if (
      r.section !== 'interests' &&
      r.section !== 'purchase_intent' &&
      r.section !== 'demographics' &&
      r.section !== 'household'
    ) {
      return false;
    }
    return true;
  });
}

// ────────────────────────────────────────────────────────────────────────
// Stage: per-observation eligibility.
// ────────────────────────────────────────────────────────────────────────

export interface PerObservationEligibilityOptions {
  /** Anchor for max_age_days computation. Defaults to `Date.now()`. */
  now?: Date;
}

/**
 * Filters individual observations against `min_confidence` (per-family) and
 * `max_age_days` (per-family). Runs BEFORE aggregation per ADR 0005 Q6 —
 * stale/low-confidence observations cannot dilute the aggregated
 * `disclosure_confidence`.
 *
 * Demographics + household rows count as `user_attribute` family.
 */
export function applyPerObservationEligibility(
  records: readonly DisclosureFactRecord[],
  policy: DisclosurePolicy,
  opts: PerObservationEligibilityOptions = {},
): DisclosureFactRecord[] {
  const now = opts.now ?? new Date();
  return records.filter((r) => {
    const family = familyOfRecord(r);
    if (family === null) return false;

    // Confidence shape gate is universal: NaN / Infinity / undefined are
    // structurally invalid in every family. Without this gate, user_
    // attribute records with NaN confidence would slip through (no v3
    // policy entry exists for user_attribute — see ADR 0005 §"Q3").
    if (typeof r.confidence !== 'number' || !Number.isFinite(r.confidence)) return false;
    if (r.confidence < 0 || r.confidence > 1) return false;

    const cfs = familySelection(policy, family);
    if (cfs === null) return true; // user_attribute family has no per-family policy selection in v3.

    if (r.confidence < cfs.min_confidence) return false;

    const ageDays = computeAgeDays(r, now);
    if (ageDays === null) return false;
    if (ageDays > cfs.max_age_days) return false;

    return true;
  });
}

function computeAgeDays(r: DisclosureFactRecord, now: Date): number | null {
  const candidate = r.last_validated ?? r.last_updated ?? r.first_observed;
  if (typeof candidate !== 'string') return null;
  const t = Date.parse(candidate);
  if (!Number.isFinite(t)) return null;
  const diff = now.getTime() - t;
  if (diff < 0) return 0; // future timestamp — treat as fresh.
  return diff / (1000 * 60 * 60 * 24);
}

function familyOfRecord(r: DisclosureFactRecord): ClaimFamily | null {
  if (r.section === 'interests') return 'interests';
  if (r.section === 'purchase_intent') return 'purchase_intent';
  if (r.section === 'demographics' || r.section === 'household') return 'user_attribute';
  return null;
}

function familySelection(policy: DisclosurePolicy, family: ClaimFamily): ClaimFamilySelection | null {
  if (family === 'interests') return policy.claim_family_selection.interests;
  if (family === 'purchase_intent') return policy.claim_family_selection.purchase_intent;
  return null; // user_attribute — no v3 policy entry.
}

// ────────────────────────────────────────────────────────────────────────
// Stage: aggregation (Stage 1 per ADR 0005 Q6).
// ────────────────────────────────────────────────────────────────────────

export interface AggregationOptions {
  /**
   * Demographics-key resolver. AT-16 calls into
   * `@ownyou/iab-classifier/canonical-demographic-keys :: groupingToField`
   * to map grouping labels → canonical OwnYou keys. Injected so the
   * pipeline stays unit-testable.
   */
  resolveCanonicalKey: (groupingValue: string) => string | undefined;
}

/**
 * Stage 1 aggregation: collapse 1..N records sharing a canonical id/key
 * into a single `AggregatedCandidate`. Aggregation is deterministic —
 * sort source records before aggregating; ties resolve id-lexicographic;
 * idempotent over re-runs.
 */
export function aggregateObservations(
  records: readonly DisclosureFactRecord[],
  opts: AggregationOptions,
): AggregatedCandidate[] {
  // Determinism: sort by a stable signature so byte-identical output is
  // reproducible across input orderings.
  const sorted = [...records].sort(stableRecordCompare);

  // Bucket by (family, canonicalId[, canonicalValue]).
  const byKey = new Map<string, DisclosureFactRecord[]>();
  for (const r of sorted) {
    const family = familyOfRecord(r);
    if (family === null) continue;

    if (family === 'interests' || family === 'purchase_intent') {
      if (typeof r.taxonomy_id !== 'number' || !Number.isFinite(r.taxonomy_id)) continue;
      if (!Number.isInteger(r.taxonomy_id)) continue;
      const canonicalId = String(r.taxonomy_id);
      // Audience Taxonomy 1.1 segtax=4 shape gate: drop taxonomy_id = 0
      // and out-of-range ids before aggregation. Mirrors the wallet-side
      // `CANONICAL_IAB_RE` in browser-extension/modules/wallet-module.js.
      if (!AUDIENCE_TAXONOMY_ID_PATTERN.test(canonicalId)) continue;
      const k = `${family}:${canonicalId}`;
      pushBucket(byKey, k, r);
      continue;
    }

    // user_attribute (demographics + household merge).
    if (typeof r.grouping_value !== 'string' || r.grouping_value.trim() === '') continue;
    const key = opts.resolveCanonicalKey(r.grouping_value.trim());
    if (typeof key !== 'string' || key === '') continue;
    if (typeof r.value !== 'string' || r.value.trim() === '') continue;
    const canonicalValue = r.value.trim();
    // Bucket per (family, key) — multi-value collapse picks the dominant value below.
    const k = `user_attribute:${key}`;
    pushBucket(byKey, k, { ...r, value: canonicalValue });
  }

  const candidates: AggregatedCandidate[] = [];
  for (const [k, bucketRecords] of [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    const colon = k.indexOf(':');
    const family = k.slice(0, colon) as ClaimFamily;
    const canonicalId = k.slice(colon + 1);

    if (family === 'interests' || family === 'purchase_intent') {
      candidates.push(buildAudienceCandidate(family, canonicalId, bucketRecords));
      continue;
    }

    // user_attribute — pick the dominant canonical value (highest
    // observation-count; ties broken by lexicographic value asc). Other
    // value records are excluded from this candidate's stats.
    const valueCounts = new Map<string, number>();
    for (const r of bucketRecords) {
      const v = r.value!;
      valueCounts.set(v, (valueCounts.get(v) ?? 0) + 1);
    }
    let dominantValue: string | null = null;
    let dominantCount = -1;
    for (const v of [...valueCounts.keys()].sort()) {
      const c = valueCounts.get(v)!;
      if (c > dominantCount) {
        dominantCount = c;
        dominantValue = v;
      }
    }
    if (dominantValue === null) continue;
    const eligibleForValue = bucketRecords.filter((r) => r.value === dominantValue);
    candidates.push(buildUserAttributeCandidate(canonicalId, dominantValue, eligibleForValue));
  }

  return candidates;
}

function pushBucket<V>(m: Map<string, V[]>, k: string, v: V): void {
  const arr = m.get(k);
  if (arr) {
    arr.push(v);
  } else {
    m.set(k, [v]);
  }
}

/** Stable record sort: by a deterministic signature. */
function stableRecordCompare(a: DisclosureFactRecord, b: DisclosureFactRecord): number {
  const sigA = stableSignature(a);
  const sigB = stableSignature(b);
  return sigA.localeCompare(sigB);
}

function stableSignature(r: DisclosureFactRecord): string {
  return [
    r.section ?? '',
    r.taxonomy_id !== undefined ? String(r.taxonomy_id) : '',
    r.grouping_value ?? '',
    r.value ?? '',
    r.last_validated ?? r.last_updated ?? r.first_observed ?? '',
    r.confidence !== undefined ? r.confidence.toFixed(8) : '',
  ].join('|');
}

function buildAudienceCandidate(
  family: 'interests' | 'purchase_intent',
  canonicalId: string,
  recs: readonly DisclosureFactRecord[],
): AggregatedCandidate {
  const meanConfidence = mean(recs.map((r) => r.confidence ?? 0));
  const totalEvidenceCount = sum(recs.map((r) => Math.max(1, r.evidence_count ?? 1)));
  const sourceSet = collectSources(recs);
  const mostRecent = mostRecentValidated(recs);
  const recRecord = mostRecentRecord(recs) ?? recs[0];
  return {
    family,
    canonicalId,
    observationCount: recs.length,
    meanConfidence,
    totalEvidenceCount,
    sourceSet,
    mostRecentLastValidated: mostRecent,
    tier_1: recRecord.tier_1,
    tier_2: recRecord.tier_2,
    tier_3: recRecord.tier_3,
    tier_4: recRecord.tier_4,
    tier_5: recRecord.tier_5,
  };
}

function buildUserAttributeCandidate(
  canonicalId: string,
  canonicalValue: string,
  recs: readonly DisclosureFactRecord[],
): AggregatedCandidate {
  const meanConfidence = mean(recs.map((r) => r.confidence ?? 0));
  const totalEvidenceCount = sum(recs.map((r) => Math.max(1, r.evidence_count ?? 1)));
  const sourceSet = collectSources(recs);
  const mostRecent = mostRecentValidated(recs);
  // Display value comes from the most-recent eligible observation that
  // mapped to the selected canonical value (per ADR 0005 Q6).
  const recRecord = mostRecentRecord(recs);
  const displayValue = recRecord?.value ?? canonicalValue;
  return {
    family: 'user_attribute',
    canonicalId,
    canonicalValue,
    displayValue,
    observationCount: recs.length,
    meanConfidence,
    totalEvidenceCount,
    sourceSet,
    mostRecentLastValidated: mostRecent,
  };
}

function collectSources(recs: readonly DisclosureFactRecord[]): Set<string> {
  const s = new Set<string>();
  for (const r of recs) {
    if (Array.isArray(r.source_ids)) {
      for (const id of r.source_ids) if (typeof id === 'string' && id) s.add(id);
    } else if (typeof r.data_source === 'string' && r.data_source) {
      s.add(r.data_source);
    }
  }
  return s;
}

function mostRecentValidated(recs: readonly DisclosureFactRecord[]): string {
  let best = '';
  let bestT = -Infinity;
  for (const r of recs) {
    const candidate = r.last_validated ?? r.last_updated ?? r.first_observed;
    if (typeof candidate !== 'string') continue;
    const t = Date.parse(candidate);
    if (Number.isFinite(t) && t > bestT) {
      best = candidate;
      bestT = t;
    }
  }
  return best;
}

function mostRecentRecord(
  recs: readonly DisclosureFactRecord[],
): DisclosureFactRecord | undefined {
  let best: DisclosureFactRecord | undefined;
  let bestT = -Infinity;
  for (const r of recs) {
    const candidate = r.last_validated ?? r.last_updated ?? r.first_observed;
    if (typeof candidate !== 'string') continue;
    const t = Date.parse(candidate);
    if (Number.isFinite(t) && t > bestT) {
      best = r;
      bestT = t;
    }
  }
  return best;
}

function mean(xs: readonly number[]): number {
  if (xs.length === 0) return 0;
  let s = 0;
  for (const x of xs) s += x;
  return s / xs.length;
}

function sum(xs: readonly number[]): number {
  let s = 0;
  for (const x of xs) s += x;
  return s;
}

// ────────────────────────────────────────────────────────────────────────
// Stage: per-aggregate eligibility.
// ────────────────────────────────────────────────────────────────────────

/**
 * Per-aggregate gate: `min_evidence_count`. User_attribute candidates are
 * naturally bounded by 1-fact-per-canonical-key and can be exempt; the
 * gate primarily applies to audience segments.
 */
export function applyPerAggregateEligibility(
  candidates: readonly AggregatedCandidate[],
  policy: DisclosurePolicy,
): AggregatedCandidate[] {
  return candidates.filter((c) => {
    if (c.family === 'user_attribute') return true;
    const cfs = familySelection(policy, c.family);
    if (!cfs) return true;
    return c.totalEvidenceCount >= cfs.min_evidence_count;
  });
}

// ────────────────────────────────────────────────────────────────────────
// Stage: disclosure_confidence (v1).
// ────────────────────────────────────────────────────────────────────────

/**
 * `confidence_aggregation_v1` per ADR 0005 Q6.tail.
 *
 *     disclosure_confidence(candidate) := mean(eligible observation confidences)
 *
 * Bumping to v2 requires a new ADR + policy version bump. The encoding
 * (`policy.confidence_encoding: "float32"`) is enforced by the BBS+ encoder
 * (AT-13/AT-14 codepath) — this function returns a JS number that round-
 * trips through `Math.fround(...)` losslessly (see `floatToFloat32`), so the
 * encoder's float32 cast at sign time produces a stable value. The wire-
 * builder leaves the pre-cast number on the payload so caller-visible
 * confidences match the source semantics.
 */
export function confidenceAggregationV1(candidate: AggregatedCandidate): number {
  return candidate.meanConfidence;
}

/**
 * Canonical float32 round-trip helper. Exposed so callers (and tests) can
 * verify the encoding contract: `floatToFloat32(disclosureConfidence)` is
 * what the BBS+ encoder signs.
 */
export function floatToFloat32(x: number): number {
  return Math.fround(x);
}

// ────────────────────────────────────────────────────────────────────────
// Stage: signal_score_v1.
// ────────────────────────────────────────────────────────────────────────

export interface SignalScoreInputs {
  candidate: AggregatedCandidate;
  /** disclosure_confidence (v1) — mean over eligible observations. */
  disclosureConfidence: number;
  /** From policy.claim_family_selection.<family>.recency_half_life_days. */
  recencyHalfLifeDays: number;
  /** Anchor for recency. Defaults to `Date.now()`. */
  now?: Date;
  /** sensitivity_penalty[id] from policy. */
  sensitivityPenalty: number;
}

/**
 * `signal_score_v1` per ADR 0005 Q6:
 *
 *     signal_score_v1 =
 *         0.55 * calibrated_confidence
 *       + 0.20 * evidence_strength
 *       + 0.15 * recency_score
 *       + 0.10 * source_diversity
 *       - sensitivity_penalty
 *
 *   evidence_strength = min(1, log10(observations + 1) / log10(11))
 *   recency_score     = exp(-days_since_last_seen / half_life)
 *   source_diversity  = min(1, evidence_sources.length / 3)
 *   calibrated_confidence (v1) = disclosure_confidence
 *
 * `sensitivity_penalty` is a placeholder hook — `policy.sensitivity_penalty`
 * is empty in v3; AT-20 lands the populated map.
 */
export function signalScoreV1(inp: SignalScoreInputs): number {
  const calibratedConfidence = inp.disclosureConfidence;
  const obs = inp.candidate.totalEvidenceCount;
  const evidenceStrength = Math.min(1, Math.log10(obs + 1) / Math.log10(11));
  const ageDays = computeAgeDaysFromCandidate(inp.candidate, inp.now ?? new Date());
  const recencyScore = ageDays === null
    ? 0
    : Math.exp(-Math.max(0, ageDays) / Math.max(1, inp.recencyHalfLifeDays));
  const sourceDiversity = Math.min(1, inp.candidate.sourceSet.size / 3);
  const raw =
    0.55 * calibratedConfidence +
    0.20 * evidenceStrength +
    0.15 * recencyScore +
    0.10 * sourceDiversity -
    Math.max(0, inp.sensitivityPenalty);
  // Floor at 0 — negative scores never improve ranking and make tie-break
  // logic harder to reason about.
  return Math.max(0, raw);
}

function computeAgeDaysFromCandidate(c: AggregatedCandidate, now: Date): number | null {
  if (!c.mostRecentLastValidated) return null;
  const t = Date.parse(c.mostRecentLastValidated);
  if (!Number.isFinite(t)) return null;
  const diff = now.getTime() - t;
  return diff < 0 ? 0 : diff / (1000 * 60 * 60 * 24);
}

// ────────────────────────────────────────────────────────────────────────
// Stage: diversity controls (audience segments only).
// ────────────────────────────────────────────────────────────────────────

interface ScoredCandidate {
  candidate: AggregatedCandidate;
  score: number;
}

/**
 * Walks score-desc; respects `max_per_tier2` + `max_per_tier3` ancestry caps
 * per `policy.claim_family_selection.<family>.diversity`. User_attribute
 * candidates skip diversity (one fact per canonical key already bounds them).
 */
export function applyDiversityControls(
  scored: readonly ScoredCandidate[],
  policy: DisclosurePolicy,
): ScoredCandidate[] {
  const sorted = [...scored].sort(scoreDesc);
  const accepted: ScoredCandidate[] = [];
  // Per-family tier counters.
  const tier2Counts = new Map<string, number>(); // key = family|tier2
  const tier3Counts = new Map<string, number>(); // key = family|tier2|tier3
  for (const s of sorted) {
    if (s.candidate.family === 'user_attribute') {
      accepted.push(s);
      continue;
    }
    const cfs = familySelection(policy, s.candidate.family);
    if (!cfs) {
      accepted.push(s);
      continue;
    }
    const t2 = s.candidate.tier_2 ?? '';
    const t3 = s.candidate.tier_3 ?? '';
    const t2Key = `${s.candidate.family}|${t2}`;
    const t3Key = `${s.candidate.family}|${t2}|${t3}`;
    const t2Count = tier2Counts.get(t2Key) ?? 0;
    const t3Count = tier3Counts.get(t3Key) ?? 0;
    if (t2 && t2Count >= cfs.diversity.max_per_tier2) continue;
    if (t3 && t3Count >= cfs.diversity.max_per_tier3) continue;
    tier2Counts.set(t2Key, t2Count + 1);
    if (t3) tier3Counts.set(t3Key, t3Count + 1);
    accepted.push(s);
  }
  return accepted;
}

function scoreDesc(a: ScoredCandidate, b: ScoredCandidate): number {
  if (b.score !== a.score) return b.score - a.score;
  // Tie-break: deterministic. By canonicalId asc.
  return a.candidate.canonicalId.localeCompare(b.candidate.canonicalId);
}

// ────────────────────────────────────────────────────────────────────────
// Stage: privacy budget (max_facts cap).
// ────────────────────────────────────────────────────────────────────────

/**
 * Hard truncate at `policy.claim_family_selection.<family>.max_facts`. The
 * privacy budget is the wire-payload bound — separate from quality gating.
 */
export function applyPrivacyBudget(
  scored: readonly ScoredCandidate[],
  policy: DisclosurePolicy,
): ScoredCandidate[] {
  const byFamily = new Map<ClaimFamily, ScoredCandidate[]>();
  for (const s of scored) {
    const arr = byFamily.get(s.candidate.family) ?? [];
    arr.push(s);
    byFamily.set(s.candidate.family, arr);
  }
  const out: ScoredCandidate[] = [];
  for (const [family, arr] of byFamily) {
    const cfs = familySelection(policy, family);
    if (!cfs || family === 'user_attribute') {
      out.push(...arr);
      continue;
    }
    out.push(...arr.slice(0, cfs.max_facts));
  }
  return out;
}

// ────────────────────────────────────────────────────────────────────────
// Reason-code derivation.
// ────────────────────────────────────────────────────────────────────────

/**
 * Derive structural reason codes from a candidate + score. Codes are drawn
 * from `policy.allowed_reason_codes` only — unknown codes never appear.
 */
export function deriveReasonCodes(
  candidate: AggregatedCandidate,
  score: number,
  scoreMedian: number,
  recencyHalfLifeDays: number,
  now: Date,
  allowed: readonly string[],
): string[] {
  const codes: string[] = [];
  const allowedSet = new Set(allowed);
  if (candidate.meanConfidence >= 0.85 && allowedSet.has('high_confidence')) codes.push('high_confidence');
  if (candidate.sourceSet.size >= 2 && allowedSet.has('multi_source')) codes.push('multi_source');
  const ageDays = computeAgeDaysFromCandidate(candidate, now);
  if (ageDays !== null && ageDays <= Math.max(1, recencyHalfLifeDays / 2) && allowedSet.has('recent')) {
    codes.push('recent');
  }
  if (candidate.totalEvidenceCount >= 5 && allowedSet.has('high_evidence_count')) codes.push('high_evidence_count');
  if (score >= scoreMedian && allowedSet.has('score_above_median')) codes.push('score_above_median');
  return codes;
}

// ────────────────────────────────────────────────────────────────────────
// Pipeline orchestration.
// ────────────────────────────────────────────────────────────────────────

export interface RunSelectionPipelineOptions {
  policy: DisclosurePolicy;
  resolveCanonicalKey: AggregationOptions['resolveCanonicalKey'];
  /** Anchor for time-based filters. Defaults to `Date.now()`. */
  now?: Date;
}

export interface SelectionPipelineOutput {
  facts: CanonicalFact[];
  /** Aggregate counts for telemetry / GDPR. */
  diagnostics: {
    raw: number;
    afterDropFilter: number;
    afterPerObservationEligibility: number;
    aggregated: number;
    afterPerAggregateEligibility: number;
    afterDiversity: number;
    afterPrivacyBudget: number;
  };
}

/**
 * Run the multi-stage selection pipeline against an array of records. The
 * caller is responsible for paginating reads first via
 * `paginateClassifications` — separating IO from logic keeps the pipeline
 * unit-testable.
 */
export function runSelectionPipeline(
  records: readonly DisclosureFactRecord[],
  opts: RunSelectionPipelineOptions,
): SelectionPipelineOutput {
  const now = opts.now ?? new Date();
  const policy = opts.policy;

  const afterDrop = applyDropAndShapeFilter(records);
  const afterEligibility = applyPerObservationEligibility(afterDrop, policy, { now });
  const aggregated = aggregateObservations(afterEligibility, {
    resolveCanonicalKey: opts.resolveCanonicalKey,
  });
  const afterAggregateEligibility = applyPerAggregateEligibility(aggregated, policy);

  // Score each candidate.
  const scored: ScoredCandidate[] = afterAggregateEligibility.map((c) => {
    const halfLife =
      c.family === 'user_attribute'
        ? 90 // user_attribute family lacks a v3 policy entry; default mirrors interests.
        : familySelection(policy, c.family)!.recency_half_life_days;
    const sensitivityPenalty =
      typeof policy.sensitivity_penalty === 'object' && policy.sensitivity_penalty
        ? policy.sensitivity_penalty[c.canonicalId] ?? 0
        : 0;
    const dc = confidenceAggregationV1(c);
    const score = signalScoreV1({
      candidate: c,
      disclosureConfidence: dc,
      recencyHalfLifeDays: halfLife,
      now,
      sensitivityPenalty,
    });
    return { candidate: c, score };
  });

  const afterDiversity = applyDiversityControls(scored, policy);
  const afterPrivacyBudget = applyPrivacyBudget(afterDiversity, policy);

  // Reason-code derivation needs a median for the score_above_median signal.
  const scoresOnly = afterPrivacyBudget.map((s) => s.score).sort((a, b) => a - b);
  const median =
    scoresOnly.length === 0
      ? 0
      : scoresOnly.length % 2 === 1
      ? scoresOnly[Math.floor(scoresOnly.length / 2)]
      : (scoresOnly[scoresOnly.length / 2 - 1] + scoresOnly[scoresOnly.length / 2]) / 2;

  // Per-family rank assignment (1-indexed within the family, score-desc).
  const byFamily = new Map<ClaimFamily, ScoredCandidate[]>();
  for (const s of afterPrivacyBudget) {
    const arr = byFamily.get(s.candidate.family) ?? [];
    arr.push(s);
    byFamily.set(s.candidate.family, arr);
  }
  for (const arr of byFamily.values()) arr.sort(scoreDesc);

  const facts: CanonicalFact[] = [];
  for (const [family, arr] of byFamily) {
    arr.forEach((s, i) => {
      const halfLife =
        family === 'user_attribute'
          ? 90
          : familySelection(policy, family)!.recency_half_life_days;
      const reasonCodes = deriveReasonCodes(
        s.candidate,
        s.score,
        median,
        halfLife,
        now,
        policy.allowed_reason_codes,
      );
      facts.push({
        family: s.candidate.family,
        canonicalId: s.candidate.canonicalId,
        canonicalValue: s.candidate.canonicalValue,
        displayValue: s.candidate.displayValue,
        disclosureConfidence: confidenceAggregationV1(s.candidate),
        tier_1: s.candidate.tier_1,
        tier_2: s.candidate.tier_2,
        tier_3: s.candidate.tier_3,
        tier_4: s.candidate.tier_4,
        tier_5: s.candidate.tier_5,
        selectionMetadata: {
          score: s.score,
          rank: i + 1,
          reason_codes: reasonCodes,
        },
      });
    });
  }

  // Deterministic output ordering: family asc, then canonicalId asc.
  facts.sort((a, b) => {
    if (a.family !== b.family) return a.family.localeCompare(b.family);
    return a.canonicalId.localeCompare(b.canonicalId);
  });

  return {
    facts,
    diagnostics: {
      raw: records.length,
      afterDropFilter: afterDrop.length,
      afterPerObservationEligibility: afterEligibility.length,
      aggregated: aggregated.length,
      afterPerAggregateEligibility: afterAggregateEligibility.length,
      afterDiversity: afterDiversity.length,
      afterPrivacyBudget: afterPrivacyBudget.length,
    },
  };
}

// ────────────────────────────────────────────────────────────────────────
// Stage: projection — strips selection metadata from the wire output.
// ────────────────────────────────────────────────────────────────────────

export interface ProjectedClaimSet {
  interests: Array<{ category: string; confidence: number; taxonomy: string }>;
  purchase_intent: Array<{ category: string; confidence: number; taxonomy: string }>;
  demographics: Record<string, string>;
  demographics_confidence: Record<string, number>;
  /** displayValue per canonical demographic key — sourced from the most-recent eligible observation. */
  demographics_display_value: Record<string, string>;
}

/**
 * Project canonical facts to the wire-bound claim-set shape.
 *
 * SECURITY GUARANTEE: this step **strips** `selectionMetadata` from every
 * fact. The wire payload (`user.ext.ownyou`, OpenRTB `user.data[]`, BBS+
 * signed messages) MUST NOT carry `score`, `rank`, or `reason_codes`. The
 * wallet's leak-guard validator hard-rejects any payload that does.
 */
export function projectToClaimSet(
  facts: readonly CanonicalFact[],
  taxonomy: string,
): ProjectedClaimSet {
  const out: ProjectedClaimSet = {
    interests: [],
    purchase_intent: [],
    demographics: {},
    demographics_confidence: {},
    demographics_display_value: {},
  };
  for (const f of facts) {
    if (f.family === 'interests' || f.family === 'purchase_intent') {
      const target = f.family === 'interests' ? out.interests : out.purchase_intent;
      target.push({
        category: f.canonicalId,
        confidence: f.disclosureConfidence,
        taxonomy,
      });
      continue;
    }
    if (f.family === 'user_attribute' && f.canonicalValue !== undefined) {
      out.demographics[f.canonicalId] = f.canonicalValue;
      out.demographics_confidence[f.canonicalId] = f.disclosureConfidence;
      if (f.displayValue !== undefined) {
        out.demographics_display_value[f.canonicalId] = f.displayValue;
      }
    }
  }
  // Deterministic ordering for stable digests.
  out.interests.sort((a, b) => a.category.localeCompare(b.category));
  out.purchase_intent.sort((a, b) => a.category.localeCompare(b.category));
  return out;
}
